import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Typography, Box } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import PropTypes from 'prop-types';

const selectedBorderWidth = 6;
const useStyles = makeStyles(({
  margin, padding, palette, spacing,
}) => ({
  root: {
    // This allows to add margin-bottom to the element
    // when it's inside a flex element with align-items=stretch property
    paddingBottom: margin.bottom,
    height: '100%',
  },
  card: ({
    selected, color, leftColor, footerColor,
  }) => ({
    boxShadow: 'none',
    borderColor: selected ? palette[color] : palette.silver,
    borderLeftColor: selected ? palette[leftColor] : palette.silver,
    borderStyle: 'solid',
    borderWidth: selected ? selectedBorderWidth : 1,
    borderBottomWidth: selected && footerColor && 0,
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    transition: 'outline 0.1s, box-shadow 0.1s',
  }),
  isSelectable: ({ color }) => ({
    cursor: 'pointer',
    '&:hover': {
      borderColor: palette[color],
      boxShadow: `3px 3px 0 0 ${color === 'blue' ? palette.primary.dark : palette.chaletGreen}`,
    },
  }),
  isSelectableWithoutFooter: {
    // This allows to keep the same height
    // for selected and not selected state
    paddingBottom: `calc(1.5rem + ${spacing(2)}px - ${selectedBorderWidth}px)`,
  },
  content: ({ selected, justifyContent }) => ({
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    justifyContent,
    padding: padding.all,
    margin: selected ? 1 - selectedBorderWidth : 0,
  }),
  footer: ({ selected, footerColor }) => ({
    color: selected ? palette.white : palette.text.primary,
    background: selected ? palette[footerColor] : 'transparent',
    padding: spacing(1),
    textAlign: 'center',
    paddingBottom: selected && footerColor === 'multicolor' ? spacing(1) : '',
  }),
  selectedFooter: {
    paddingBottom: spacing(1) - selectedBorderWidth,
  },
}));

const InfoCard = React.memo(({
  color,
  footerColor,
  leftColor,
  children,
  footer,
  onClick,
  selected,
  justifyContent,
}) => {
  const { t } = useTranslation();
  const classes = useStyles({
    color, selected, justifyContent, footerColor, leftColor,
  });
  let footerEl;
  if (!footer && selected) {
    footerEl = t('selected').toUpperCase();
  } else if (footer) {
    footerEl = footer;
  }

  const attrs = {
    className: clsx(
      classes.card,
      {
        [classes.isSelectable]: onClick,
      },
      {
        [classes.isSelectableWithoutFooter]: onClick && !footerEl,
      },
      {
        [classes.noBorderBottom]: selected && footerColor,
      },
    ),
  };

  if (onClick) {
    attrs.onClick = onClick;
  }

  return (
    <Box className={classes.root}>
      <Box {...attrs}>
        <Box className={classes.content}>
          {children}
        </Box>
        {footerEl && (
          <Box className={clsx(classes.footer, { [classes.selectedFooter]: selected })}>
            <Typography
              component="div"
              variant="h4"
              className={classes.title}
            >
              {footerEl}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
});

InfoCard.defaultProps = {
  color: 'blue',
  leftColor: 'blue',
  footerColor: 'blue',
  footer: '',
  onClick: null,
  selected: false,
  justifyContent: 'flex-start',
};

InfoCard.propTypes = {
  color: PropTypes.oneOf(['green', 'blue', 'multicolor']),
  leftColor: PropTypes.oneOf(['green', 'blue', 'multicolor']),
  footerColor: PropTypes.oneOf(['green', 'blue', 'multicolor']),
  children: PropTypes.oneOfType([
    PropTypes.element,
    PropTypes.arrayOf(PropTypes.element),
  ]).isRequired,
  footer: PropTypes.oneOfType([
    PropTypes.element,
    PropTypes.string,
  ]),
  onClick: PropTypes.func,
  selected: PropTypes.bool,
  justifyContent: PropTypes.string,
};

export default InfoCard;
